/**
 * Device identity + child session token management (localStorage with sessionStorage fallback).
 */
import { generateToken, sha256Hex } from "./crypto";

const DEVICE_ID_KEY = "flyt_device_id";
const SESSION_TOKEN_KEY = "flyt_child_session_token";
const SESSION_CHILD_ID_KEY = "flyt_child_session_child_id";
const DEVICE_MODE_KEY = "flyt_device_mode";

function getStorage(): Storage {
  try {
    localStorage.setItem("__test", "1");
    localStorage.removeItem("__test");
    return localStorage;
  } catch {
    return sessionStorage;
  }
}

/** Get or create a persistent device ID */
export function getOrCreateDeviceId(): string {
  const storage = getStorage();
  let id = storage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = generateToken(32);
    storage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

export async function getDeviceIdHash(): Promise<string> {
  return sha256Hex(getOrCreateDeviceId());
}

/** Store child session after successful claim */
export function storeChildSession(sessionToken: string, childId: string) {
  const storage = getStorage();
  storage.setItem(SESSION_TOKEN_KEY, sessionToken);
  storage.setItem(SESSION_CHILD_ID_KEY, childId);
  storage.setItem(DEVICE_MODE_KEY, "child");
}

/** Get stored child session */
export function getChildSession(): { sessionToken: string; childId: string } | null {
  const storage = getStorage();
  const sessionToken = storage.getItem(SESSION_TOKEN_KEY);
  const childId = storage.getItem(SESSION_CHILD_ID_KEY);
  if (!sessionToken || !childId) return null;
  return { sessionToken, childId };
}

/** Whether a child session exists */
export function hasChildSession(): boolean {
  return getChildSession() !== null;
}

/** Whether this device is (or was) a child device */
export function isChildDevice(): boolean {
  return hasChildSession() || getStorage().getItem(DEVICE_MODE_KEY) === "child";
}

/** Clear child session (e.g. on revoke or validation failure) */
export function clearChildSession() {
  const storage = getStorage();
  storage.removeItem(SESSION_TOKEN_KEY);
  storage.removeItem(SESSION_CHILD_ID_KEY);
  storage.removeItem(DEVICE_MODE_KEY);
}
